import React, { Component } from "react";
import PropTypes from "prop-types";
import { createTreatmentsFetcher } from "../../api/treatment";
import themePropTypes from "../theme-prop-types";
import {
  isScrollNeeded,
  getScrollIndexMovingUp,
  containsNonEmptyOptions,
  AUTOCOMPLETE_TEST_IDS,
  AUTOCOMPLETE_TERM_NAME,
  KEY_CODES,
  AUTOCOMPLETE_CLASSNAMES
} from "../../utils/autocomplete-utils";
import { EXPANDED_CLASSNAME, INVALID_CLASSNAME } from "../../utils/constants";

const AutocompleteIcon = React.forwardRef(
  ({ className, ariaLabel = "", onClick, tabIndex = "0", settings }, ref) => (
    <span
      role={onClick ? "button" : "search"}
      ref={ref}
      tabIndex={tabIndex}
      className={className}
      onClick={onClick}
      onKeyPress={onClick}
      aria-label={ariaLabel || settings?.translations?.remove || "Remove value"}
    />
  )
);

class Autocomplete extends Component {
  fetchTreatments = createTreatmentsFetcher();

  constructor(props) {
    super(props);
    this.state = {
      activeSuggestion: null,
      filteredSuggestions: props.suggestions,
      showSuggestions: false,
      userInput: props.value || props.defaultValue,
      valueFromCurrentLocation: false,
      initialLocationRequest:
        "initialLocationRequest" in props ? props.initialLocationRequest : true,
      focusedItem: null,
      defaultValue: props.defaultValue
    };
    this.wrapperRef = React.createRef();
    this.textInput = React.createRef();
    this.clearInputButton = React.createRef();
    this.suggestionsRefs = {};
  }

  static getDerivedStateFromProps(
    { suggestions, value, locationField, allowManual, settings, defaultValue },
    state
  ) {
    const FROM_LOCATION =
      settings?.translations?.from_current_location || "From Current Location";

    if (defaultValue !== state.defaultValue) {
      return {
        ...state,
        defaultValue,
        userInput: defaultValue
      };
    }

    if (
      state.initialLocationRequest &&
      locationField &&
      (value === "" || value.value === "")
    ) {
      return {
        activeSuggestion: null,
        filteredSuggestions: suggestions,
        showSuggestions: false,
        userInput: FROM_LOCATION,
        initialLocationRequest: false
      };
    }

    if (
      state.userInput === FROM_LOCATION ||
      state.userInput === value ||
      state.valueFromCurrentLocation
    ) {
      const suggestion = suggestions.find(
        suggestionItem => suggestionItem.value === value
      );
      return {
        ...state,
        userInput:
          suggestion?.label || (allowManual ? value.value ?? value : ""),
        valueFromCurrentLocation: state.userInput === FROM_LOCATION,
        initialLocationRequest: false
      };
    }

    return null;
  }

  updateTreatmentsList = userInput => {
    this.fetchTreatments(userInput)
      .then(treatments => {
        const filteredSuggestions = treatments.data?.reduce(
          (uniqueTreatment, data) => {
            if (
              !uniqueTreatment.some(treatment => treatment.label === data.name)
            ) {
              return [...uniqueTreatment, { label: data.name, value: data }];
            }
            return uniqueTreatment;
          },
          []
        );
        this.setState({
          activeSuggestion: null,
          filteredSuggestions,
          showSuggestions: filteredSuggestions.length > 0
        });
      })
      .catch(() => {}); // dummy catch to catch abort
  };

  updateAsyncList = userInput => {
    this.props
      .asyncFetchPromise(userInput)
      .then(results => {
        this.setState({
          activeSuggestion: null,
          filteredSuggestions: results,
          showSuggestions: results.length > 0
        });
      })
      .catch(() => {}); // dummy catch to catch abort
  };

  onChange = e => {
    const {
      suggestions,
      allowManual,
      handleChange,
      asyncFetch = false
    } = this.props;
    const userInput = e.currentTarget.value;
    if (allowManual) {
      handleChange(userInput);
    }
    this.setState({
      userInput
    });
    if (this.props.name === AUTOCOMPLETE_TERM_NAME) {
      if (userInput.length) {
        this.updateTreatmentsList(userInput);
      }
    } else if (asyncFetch) {
      if (userInput.length) {
        this.updateAsyncList(userInput.trim());
      }
    } else {
      const filteredSuggestions = suggestions.filter(
        suggestion =>
          suggestion.label.toLowerCase().indexOf(userInput.toLowerCase()) > -1
      );
      this.setState({
        activeSuggestion: null,
        filteredSuggestions,
        showSuggestions: true
      });
    }
  };

  onMouseDown = (e, value) => {
    this.setState(
      {
        activeSuggestion: null,
        filteredSuggestions: this.props.suggestions,
        showSuggestions: false,
        userInput: e.currentTarget.innerText
      },
      () => {
        this.props.handleChange(value);
        if (this.clearInputButton?.current) {
          this.clearInputButton.current.focus();
        }
      }
    );
    e.currentTarget.blur();
    e.preventDefault();
  };

  onKeyDown = e => {
    const {
      activeSuggestion: activeSuggestionIndex,
      filteredSuggestions,
      showSuggestions
    } = this.state;
    const { onEnterDown, handleChange } = this.props;
    const setActiveSuggestion = sign =>
      sign === "-"
        ? activeSuggestionIndex - 1
        : sign === "+"
        ? activeSuggestionIndex !== null
          ? activeSuggestionIndex + 1
          : 0
        : 0;

    // User pressed the enter key, update the input and close the
    // suggestions
    if (e.keyCode === KEY_CODES.enter) {
      if (!filteredSuggestions[activeSuggestionIndex]) {
        onEnterDown(e);
      } else {
        this.setState(
          {
            activeSuggestion: null,
            showSuggestions: false,
            userInput: filteredSuggestions[activeSuggestionIndex].label
          },
          () => {
            handleChange(filteredSuggestions[activeSuggestionIndex].value);
            if (this.clearInputButton?.current) {
              this.clearInputButton.current.focus();
            }
          }
        );
        e.currentTarget.blur();
        e.preventDefault();
      }
    }
    // User pressed the up arrow, decrement the index
    else if (e.keyCode === KEY_CODES.arrowUp) {
      if (activeSuggestionIndex === 0 || activeSuggestionIndex === null) {
        return;
      }

      this.setState({
        activeSuggestion: setActiveSuggestion("-"),
        focusedItem: filteredSuggestions[setActiveSuggestion("-")]?.value
      });

      if (isScrollNeeded(activeSuggestionIndex)) {
        this.scrollListTo(
          filteredSuggestions[getScrollIndexMovingUp(activeSuggestionIndex)]
            ?.value
        );
      }
    }
    // User pressed the down arrow, increment the index
    else if (e.keyCode === KEY_CODES.arrowDown) {
      if (!showSuggestions) {
        this.showSuggestionsList();
      }

      if (isScrollNeeded(activeSuggestionIndex)) {
        this.scrollListTo(filteredSuggestions[activeSuggestionIndex]?.value);
      }

      if (activeSuggestionIndex - 1 === filteredSuggestions.length) {
        return;
      }

      this.setState({
        activeSuggestion: setActiveSuggestion("+"),
        focusedItem: filteredSuggestions[setActiveSuggestion("+")]?.value
      });
    }
  };

  setExpanded = showSuggestions => this.setState({ showSuggestions });

  showSuggestionsList = () => {
    this.setExpanded(true);
  };

  optionClassnames = (suggestion, activeSuggestion, index) => {
    return `${
      index === activeSuggestion ? AUTOCOMPLETE_CLASSNAMES.activeSuggestion : ""
    }${!suggestion.value ? "hidden-option" : ""}`;
  };

  getSuggestionsListComponent = () => {
    const {
      onMouseDown,
      state: { activeSuggestion, filteredSuggestions, showSuggestions },
      props: { listId }
    } = this;
    const suggestions = filteredSuggestions.length
      ? filteredSuggestions
      : this.props.suggestions;

    if (showSuggestions && containsNonEmptyOptions(suggestions)) {
      return (
        <ul
          className='autocomplete-suggestions-web-sdk'
          id={listId}
          role='listbox'
          tabIndex='-1'
          aria-expanded={activeSuggestion}
          data-testid={AUTOCOMPLETE_TEST_IDS.suggestionsList}
        >
          {suggestions.map((suggestion, index) => (
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
            <li
              className={this.optionClassnames(
                suggestion,
                activeSuggestion,
                index
              )}
              key={suggestion.value}
              onMouseDown={e => onMouseDown(e, suggestion.value)}
              id={suggestion.value}
              role='option'
              aria-selected={index === activeSuggestion || false}
              ref={e => {
                this.suggestionsRefs[suggestion.value] = e;
              }}
              data-testid={AUTOCOMPLETE_TEST_IDS.singleSuggestionPattern(index)}
            >
              {suggestion.label}
            </li>
          ))}
        </ul>
      );
    }
  };

  simulateEnterDown = e => {
    if (this.props.onEnterDown) {
      this.props.onEnterDown(e, this.state.userInput);
    }
    this.setState({ showSuggestions: false });
  };

  clearInput = e => {
    this.setState(
      {
        activeSuggestion: null,
        showSuggestions: false,
        userInput: ""
      },
      () => {
        this.simulateEnterDown(e);
        this.props.handleChange();
        this.textInput.current.focus();
      }
    );
  };

  onFocusHandler = () => {
    if (this.props.value === "") return;
    this.props.handleChange("");
    this.setState({
      userInput: "",
      activeSuggestion: null,
      filteredSuggestions: this.props.suggestions
    });
  };

  scrollListTo(id) {
    const appropriateRef = this.suggestionsRefs[id];

    if (appropriateRef) {
      appropriateRef.scrollIntoView();
    }
  }

  render() {
    const {
      onChange,
      onKeyDown,
      setExpanded,
      state: { showSuggestions, focusedItem, userInput },
      props: { labelId, inputId, listId, placeholder },
      clearInput,
      textInput,
      clearInputButton
    } = this;
    const { name, showMagnifyingGlassIcon, showCrossIcon, settings, label } =
      this.props;
    const isInvalid = this.props.invalid ? INVALID_CLASSNAME : "";
    const isExpanded =
      showSuggestions && !this.state.userInput ? EXPANDED_CLASSNAME : "";
    const isChevron = !userInput ? "autocomplete-web-sdk-chevron" : "";
    const deleteButtonDescription =
      this.props.settings?.translations?.delete_state_button || "";

    const blurHandler = e => {
      if (
        this.wrapperRef.current &&
        !this.wrapperRef.current.contains(e.relatedTarget)
      ) {
        setExpanded(false);
      }
    };

    return (
      <div
        className='autocomplete-wrapper-web-sdk'
        ref={this.wrapperRef}
        onBlur={blurHandler}
      >
        {showMagnifyingGlassIcon ? (
          <AutocompleteIcon
            className='autocomplete-icon-web-sdk magnifying-glass'
            tabIndex={-1}
            settings={settings}
          />
        ) : null}
        <input
          data-testid={AUTOCOMPLETE_TEST_IDS.inputPattern(name)}
          className={`autocomplete-web-sdk ${isChevron} ${isExpanded} ${isInvalid}`}
          type='text'
          id={inputId}
          ref={textInput}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onClick={() => setExpanded(!showSuggestions)}
          onFocus={() => this.onFocusHandler()}
          value={userInput}
          name={`autocomplete-${name}`}
          title={label}
          role='combobox'
          aria-expanded={showSuggestions}
          aria-controls={listId}
          aria-labelledby={labelId}
          aria-haspopup='listbox'
          tabIndex='0'
          aria-autocomplete='list'
          aria-owns={listId}
          aria-activedescendant={focusedItem}
          placeholder={placeholder}
        />
        {showCrossIcon && userInput && name !== AUTOCOMPLETE_TERM_NAME ? (
          <AutocompleteIcon
            className='autocomplete-icon-web-sdk cross'
            onClick={clearInput}
            ref={clearInputButton}
            ariaLabel={deleteButtonDescription}
          />
        ) : null}
        {this.getSuggestionsListComponent()}
      </div>
    );
  }
}

AutocompleteIcon.propTypes = {
  className: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string,
  tabIndex: PropTypes.number,
  settings: PropTypes.objectOf(PropTypes.object)
};
Autocomplete.propTypes = themePropTypes.autocomplete;
AutocompleteIcon.displayName = "AutocompleteIcon";

export default Autocomplete;
